import { ReactElement, useMemo } from "react";
import {
  OrganisationFeatureFlags,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";

import { classNames } from "../../../../common/lib/classNames";
import { env } from "../../../../common/lib/env";
import { useUser } from "../../../hooks/useUser";
import { PendingInvitations } from "../../Common/PendingInvitations";
import { useSettings } from "../../../hooks/useSettings";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";

import { Insights } from "./Insights";
import { BadContacts } from "./BadContacts";
import { EmailDeliveryWidget } from "./EmailDeliveryWidget";
import { InvoiceHistory } from "./InvoiceHistory";
import { BadInvoices } from "./BadInvoices";
import { ActionableTasks } from "./ActionableTasks";
import { LogoHelper } from "./LogoHelper";
import { PaymentForecast } from "./PaymentForecast";
import { AiSuggestions } from "./AiSuggestions";

interface WidgetConfig {
  component: () => ReactElement;
  span: {
    small: 1 | 2 | `full`;
    desktop: 1 | 2 | 3 | 4 | 5 | 6 | `full`;
  };
}

export function WidgetsRoot() {
  const { data: user } = useUser();
  const { data: settings } = useSettings();
  const currentOrg = useGetSelectedOrganisation();

  /** TODO - This will be where users customise dash? */
  const widgets: WidgetConfig[] = useMemo(() => {
    const w: WidgetConfig[] = [
      {
        component: () => <Insights />,
        span: {
          small: `full`,
          desktop: `full`,
        },
      },
      {
        component: ActionableTasks,
        span: {
          small: 2,
          desktop: 3,
        },
      },
      {
        component: InvoiceHistory,
        span: {
          small: 2,
          desktop: 3,
        },
      },
      {
        component: BadContacts,
        span: {
          small: 2,
          desktop: 3,
        },
      },
      {
        component: BadInvoices,
        span: {
          small: 2,
          desktop: 3,
        },
      },
      {
        component: EmailDeliveryWidget,
        span: {
          small: 2,
          desktop: 3,
        },
      },
      {
        component: PaymentForecast,
        span: {
          small: 2,
          desktop: 3,
        },
      },
    ];

    // AI suggestions sit under the insights strip, for flagged orgs only
    if (currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_SUGGESTIONS)) {
      w.splice(1, 0, {
        component: AiSuggestions,
        span: {
          small: `full`,
          desktop: `full`,
        },
      });
    }

    // When no logo, add the logo widget after ActionableTasks
    if (!settings?.logoUrl && accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_LOGO_IMPORT)) {
      w.splice(5, 0, {
        component: LogoHelper,
        span: {
          small: 2,
          desktop: 3,
        },
      });
    }

    return w;
  }, [user, env, Insights, BadContacts, InvoiceHistory, BadInvoices, EmailDeliveryWidget, ActionableTasks, settings, LogoHelper, currentOrg]);

  // Need to declare for CSS gen
  const colClassNamesSmall = {
    1: `col-span-1`,
    2: `col-span-2`,
    full: `col-span-2`,
  };

  const colClassNamesDesktop = {
    1: `lg:col-span-1`,
    2: `lg:col-span-2`,
    3: `lg:col-span-3`,
    4: `lg:col-span-4`,
    5: `lg:col-span-5`,
    6: `lg:col-span-6`,
    [`full`]: `lg:col-span-6`,
  };

  return (
    // 4 columns, except on small/small screen
    <>
      <PendingInvitations showContentOnly={ false } />
      <div className={ `grid grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4 grow` }>
        {
          widgets.map((widget, i) => (
            <div
              key={ i }
              className={
                classNames(
                  colClassNamesSmall[widget.span.small],
                  colClassNamesDesktop[widget.span.desktop],
                )
              }>
              <widget.component/>
            </div>
          ))
        }
      </div>
    </>
  );
}
